/**
 * Disclaimer Page
 * Critical legal disclaimers and limitations of CalWMP service
 */

import { Link } from 'react-router-dom';

export function Disclaimer() {
  const lastUpdated = '2025-09-30';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Disclaimer</h1>

      <p className="text-sm text-neutral-600 mb-8">
        <strong>Last Updated:</strong> {lastUpdated}
      </p>

      {/* Critical Warnings */}
      <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-6 mb-8">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-bold text-amber-900 mb-2">IMPORTANT: READ CAREFULLY</h2>
            <p className="text-amber-900 leading-relaxed">
              CalWMP is a tool to assist in creating waste management plans. It is <strong>NOT</strong> a substitute for professional advice, and using CalWMP does <strong>NOT</strong> guarantee regulatory compliance or acceptance by any jurisdiction.
            </p>
          </div>
        </div>
      </div>

      <div className="prose prose-neutral max-w-none">
        {/* NOT LEGAL OR REGULATORY ADVICE */}
        <section className="mb-8">
          <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-6 mb-4">
            <h2 className="text-2xl font-bold text-orange-900 mb-3">NOT LEGAL OR REGULATORY ADVICE</h2>
            <p className="text-orange-900 leading-relaxed mb-3">
              <strong>CalWMP does NOT provide legal, regulatory, or professional advice.</strong> The information, tools, and generated plans provided by CalWMP are for informational and planning purposes only. They should not be construed as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-orange-900">
              <li>Legal advice or legal interpretation of CalGreen 5.408 or any other regulation</li>
              <li>Engineering or professional design recommendations</li>
              <li>Regulatory compliance certification or approval</li>
              <li>Advice specific to your project, jurisdiction, or circumstances</li>
              <li>A replacement for consultation with qualified professionals</li>
            </ul>
            <p className="text-orange-900 leading-relaxed mt-3">
              <strong>You should always consult with qualified professionals</strong> including attorneys, engineers, architects, and regulatory specialists who understand your specific project requirements and local jurisdiction rules.
            </p>
          </div>
        </section>

        {/* NOT GOVERNMENT AFFILIATED */}
        <section className="mb-8">
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
            <h2 className="text-2xl font-bold text-yellow-900 mb-3">NOT GOVERNMENT AFFILIATED</h2>
            <p className="text-yellow-900 leading-relaxed mb-3">
              <strong>CalWMP is NOT affiliated with, endorsed by, or connected to:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-yellow-900 mb-3">
              <li>The State of California or any California state agency</li>
              <li>CalRecycle (California Department of Resources Recycling and Recovery)</li>
              <li>Any city, county, or local jurisdiction</li>
              <li>Any building department or regulatory authority</li>
              <li>Any professional licensing board or regulatory body</li>
            </ul>
            <p className="text-yellow-900 leading-relaxed">
              CalWMP is an independent, private service. Any references to government regulations, standards, or requirements are provided for informational purposes only and do not constitute official guidance.
            </p>
          </div>
        </section>

        {/* Professional Review Required */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Professional Review Required</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
            <p className="text-blue-900 mb-3">
              <strong>All plans generated by CalWMP MUST be reviewed by qualified professionals before submission.</strong>
            </p>
            <p className="text-blue-900 leading-relaxed">
              Plans should be reviewed and validated by professionals with expertise in your specific project type, location, and regulatory requirements. This may include architects, engineers, waste consultants, environmental specialists, and legal counsel familiar with local jurisdiction requirements.
            </p>
          </div>

          <p className="text-neutral-700 leading-relaxed mb-4">
            <strong>Why Professional Review is Essential:</strong>
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Local Variations:</strong> Jurisdictions have unique interpretations and additional requirements beyond CalGreen 5.408</li>
            <li><strong>Project Specifics:</strong> Your project may have unique circumstances requiring specialized solutions</li>
            <li><strong>Regulatory Changes:</strong> Regulations evolve, and professionals stay current with updates</li>
            <li><strong>Technical Accuracy:</strong> Professionals can verify calculations, feasibility, and implementation details</li>
            <li><strong>Liability Protection:</strong> Professional review provides accountability and expertise</li>
          </ul>
        </section>

        {/* No Guarantee of Compliance */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">No Guarantee of Compliance</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            <strong>CalWMP makes no guarantee that plans created using our service will:</strong>
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Meet the requirements of CalGreen 5.408 or any other regulation</li>
            <li>Be accepted by your local jurisdiction or building department</li>
            <li>Result in permit approval or regulatory compliance</li>
            <li>Satisfy project-specific requirements or specifications</li>
            <li>Be suitable for your particular project type, size, or location</li>
            <li>Be complete, accurate, or current with latest regulatory changes</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            <strong>Jurisdictional Variations:</strong> Different cities, counties, and local jurisdictions have varying interpretations of CalGreen 5.408, additional local ordinances, and unique submission requirements. CalWMP cannot account for all local variations. You must verify requirements with your specific jurisdiction.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            <strong>Plan Acceptance:</strong> Building departments and regulatory authorities have sole discretion in accepting or rejecting plans. CalWMP has no control over these decisions and cannot guarantee acceptance.
          </p>
        </section>

        {/* Data Accuracy Limitations */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Data Accuracy Limitations</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Information Accuracy</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            While we strive to provide accurate and current information, CalWMP makes no representations or warranties regarding:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Regulatory Information:</strong> Completeness or accuracy of CalGreen 5.408 requirements or interpretations</li>
            <li><strong>Waste Stream Data:</strong> Generation rates, diversion rates, or material characteristics</li>
            <li><strong>Calculations:</strong> Formulas, percentages, or compliance calculations</li>
            <li><strong>Templates:</strong> Suitability of plan templates for specific project types</li>
            <li><strong>References:</strong> Accuracy of links, citations, or external resources</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">User-Provided Data</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP generates plans based on information you provide. The accuracy and completeness of your plan depends entirely on the accuracy and completeness of your input data. CalWMP:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Cannot verify the accuracy of data you enter</li>
            <li>Cannot determine if your input is appropriate for your project</li>
            <li>Cannot validate technical feasibility or compliance</li>
            <li>Is not responsible for errors resulting from incorrect user input</li>
          </ul>

          <p className="text-neutral-700 leading-relaxed">
            <strong>Garbage In, Garbage Out:</strong> The quality of your plan output directly depends on the quality of your input. Professional review can help identify and correct input errors.
          </p>
        </section>

        {/* No Warranty */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">No Warranty</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP is provided &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; without any warranties of any kind, either express or implied, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Merchantability:</strong> Fitness for a particular purpose</li>
            <li><strong>Accuracy:</strong> Correctness, completeness, or reliability of information</li>
            <li><strong>Non-Infringement:</strong> Freedom from intellectual property claims</li>
            <li><strong>Results:</strong> Achievement of compliance or plan acceptance</li>
            <li><strong>Availability:</strong> Uninterrupted or error-free service operation</li>
            <li><strong>Data Security:</strong> Complete protection against data loss or breach</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            We disclaim all warranties to the fullest extent permitted by law. Use of CalWMP is at your own risk.
          </p>
        </section>

        {/* Limitation of Liability */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Limitation of Liability</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            To the maximum extent permitted by law, CalWMP and its operators, owners, employees, and affiliates shall not be liable for any:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Direct Damages:</strong> Costs of plan rejection, permit delays, or project impacts</li>
            <li><strong>Indirect Damages:</strong> Lost profits, business interruption, or lost opportunities</li>
            <li><strong>Consequential Damages:</strong> Resulting from use or inability to use CalWMP</li>
            <li><strong>Regulatory Penalties:</strong> Fines, citations, or enforcement actions</li>
            <li><strong>Professional Costs:</strong> Expenses for consultants, revisions, or remediation</li>
            <li><strong>Data Loss:</strong> Loss or corruption of plan data or information</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            This limitation applies even if CalWMP has been advised of the possibility of such damages. Some jurisdictions do not allow limitation of liability for consequential damages, so this limitation may not apply to you.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 my-4">
            <p className="text-amber-900 leading-relaxed">
              <strong>Your Maximum Remedy:</strong> If you are dissatisfied with CalWMP or believe any information is inaccurate, your sole remedy is to discontinue use of the service.
            </p>
          </div>
        </section>

        {/* Regulatory Changes */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Regulatory Changes</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Building codes, environmental regulations, and compliance requirements change regularly. CalWMP:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>May not reflect the most current version of CalGreen or other regulations</li>
            <li>Cannot guarantee information is current as of your plan creation date</li>
            <li>May not incorporate recent regulatory updates, amendments, or interpretations</li>
            <li>Cannot predict future regulatory changes affecting your project</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            <strong>It is your responsibility to:</strong>
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Verify current regulatory requirements with your jurisdiction</li>
            <li>Confirm CalWMP information is current and applicable</li>
            <li>Monitor for regulatory changes affecting your project</li>
            <li>Update plans as needed to reflect current requirements</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            Professional consultants can help ensure your plan reflects current regulations and jurisdictional requirements.
          </p>
        </section>

        {/* User Responsibility */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">User Responsibility</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            <strong>By using CalWMP, you acknowledge and agree that you are solely responsible for:</strong>
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Plan Accuracy and Completeness</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Accuracy of all data and information you enter</li>
            <li>Completeness of plans for your specific project needs</li>
            <li>Technical feasibility of proposed waste management strategies</li>
            <li>Suitability of plans for your project type and location</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Professional Review</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Engaging qualified professionals to review your plans</li>
            <li>Verifying compliance with applicable regulations</li>
            <li>Obtaining necessary professional stamps, seals, or certifications</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Jurisdictional Requirements</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Researching and understanding your jurisdiction&apos;s specific requirements</li>
            <li>Contacting your local building department for guidance</li>
            <li>Submitting plans according to local procedures and formats</li>
            <li>Responding to jurisdiction questions or revision requests</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Implementation</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Proper implementation of waste management strategies</li>
            <li>Compliance with all applicable laws and regulations</li>
            <li>Meeting contractual and project obligations</li>
            <li>Monitoring and documentation of compliance</li>
          </ul>
        </section>

        {/* Third-Party Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Third-Party Resources and Links</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP may provide links to external websites, reference materials, or third-party resources. These links are provided for convenience only. CalWMP:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Does not control, endorse, or assume responsibility for third-party content</li>
            <li>Makes no warranties about the accuracy or reliability of external resources</li>
            <li>Is not responsible for the availability of external sites</li>
            <li>Has no liability for your use of third-party resources</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            You access third-party resources at your own risk and subject to their terms and conditions.
          </p>
        </section>

        {/* CalGreen Overview */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Educational Resources</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP provides educational content about CalGreen 5.408 and waste management planning. This information:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Is intended for general educational purposes only</li>
            <li>Should not be considered comprehensive or authoritative</li>
            <li>May be simplified or summarized for clarity</li>
            <li>Should be verified with official regulatory sources</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            <strong>For authoritative information:</strong> Consult the official California Building Standards Code (Title 24, Part 11), CalRecycle resources, and your local jurisdiction&apos;s building department. See our <Link to="/calgreen" className="text-primary-600 hover:text-primary-700 underline">CalGreen Overview</Link> page for reference links.
          </p>
        </section>

        {/* Service Availability */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Service Availability</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP may modify, suspend, or discontinue any aspect of the service at any time without notice. We do not guarantee:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Continuous availability of the service</li>
            <li>Access to your plans at any specific time</li>
            <li>Preservation of features or functionality</li>
            <li>Advance notice of changes or discontinuation</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            <strong>Protect Your Work:</strong> Regularly download and save copies of your plans. CalWMP is not responsible for data loss resulting from service interruptions, modifications, or discontinuation. See our <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</Link> for data retention details.
          </p>
        </section>

        {/* Indemnification */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Indemnification</h2>
          <p className="text-neutral-700 leading-relaxed">
            You agree to indemnify, defend, and hold harmless CalWMP and its operators, owners, employees, and affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Your use of CalWMP or plans generated by CalWMP</li>
            <li>Your violation of these disclaimers or our Terms of Service</li>
            <li>Your violation of applicable laws or regulations</li>
            <li>Plan rejection, regulatory non-compliance, or project issues</li>
            <li>Claims by third parties related to your use of CalWMP</li>
          </ul>
        </section>

        {/* Agreement to Disclaimers */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Agreement to Disclaimers</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
            <p className="text-blue-900 leading-relaxed mb-3">
              <strong>By using CalWMP, you explicitly acknowledge and agree that:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-blue-900">
              <li>You have read and understood all disclaimers on this page</li>
              <li>You will not rely on CalWMP as legal or professional advice</li>
              <li>You will obtain professional review before submitting plans</li>
              <li>You understand CalWMP provides no compliance guarantees</li>
              <li>You accept all limitations of liability described herein</li>
              <li>You are responsible for verifying accuracy and compliance</li>
            </ul>
          </div>
          <p className="text-neutral-700 leading-relaxed">
            If you do not agree to these disclaimers, do not use CalWMP. Your continued use constitutes acceptance of these terms.
          </p>
        </section>

        {/* Severability */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Severability</h2>
          <p className="text-neutral-700 leading-relaxed">
            If any provision of this Disclaimer is found to be invalid or unenforceable by a court of competent jurisdiction, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Questions About This Disclaimer</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If you have questions about this Disclaimer or need clarification about CalWMP&apos;s limitations, please contact us:
          </p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6">
            <p className="text-neutral-900 mb-2">
              <strong>Email:</strong> <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>
            </p>
            <p className="text-neutral-900">
              <strong>Response Time:</strong> We aim to respond to all inquiries within 5 business days.
            </p>
          </div>
          <p className="text-neutral-700 leading-relaxed mt-4 text-sm italic">
            Note: Questions about disclaimers can be answered. However, we cannot provide project-specific advice, regulatory interpretations, or compliance guidance for your particular situation.
          </p>
        </section>

        {/* Related Documents */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Related Documents</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            This Disclaimer should be read in conjunction with:
          </p>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <Link to="/terms" className="text-primary-600 hover:text-primary-700 underline">
                Terms of Service
              </Link>
              {' '}&mdash; Legal terms governing use of CalWMP
            </li>
            <li>
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>
              {' '}&mdash; How we handle your data and information
            </li>
            <li>
              <Link to="/faq" className="text-primary-600 hover:text-primary-700 underline">
                Frequently Asked Questions
              </Link>
              {' '}&mdash; Common questions about using CalWMP
            </li>
            <li>
              <Link to="/calgreen" className="text-primary-600 hover:text-primary-700 underline">
                CalGreen 5.408 Overview
              </Link>
              {' '}&mdash; Educational information about the regulation
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
          <p className="text-amber-900 text-sm leading-relaxed">
            <strong>Final Reminder:</strong> CalWMP is a planning tool, not a compliance certification service. All plans must be reviewed by qualified professionals and verified with your local jurisdiction before submission. Use professional judgment and seek expert guidance for your specific project needs.
          </p>
        </div>
        <p className="text-sm text-neutral-600">
          This Disclaimer was last updated on {lastUpdated}. We may update these disclaimers to reflect changes in our service or legal requirements. Check this page periodically for updates.
        </p>
      </div>
    </div>
  );
}